import { InfiniscrollContainer } from '@/shared/components/export';
import { useSubscription } from '@/core/messaging/export';
import { MarketSignal } from '@/modules/market-signals/model/market-signals/market-signal';
import marketSignalApi from '@/modules/market-signals/api/market-signal-api';
import dayjs from 'dayjs';
import { useContext, useEffect, useState } from 'react';
import { useTimer } from '@/shared/hooks/export';
import {
  MarketSignalContext,
  MarketSignalContextType,
} from '@/modules/market-signals/contexts/market-signal-context';
import { uniqueById } from '@/modules/market-signals/util/unique-by-id';

export default function MarketSignalScrollerPanel({
  selectedStream,
  paused,
  renderSignal,
  onMissedUpdate,
}: {
  selectedStream: string;
  paused: boolean;
  renderSignal: (m: MarketSignal, index: number) => JSX.Element;
  onMissedUpdate: () => void;
}) {
  const { updateNotificationText } = useContext<MarketSignalContextType>(MarketSignalContext);
  const [signals, setSignals] = useState<MarketSignal[]>([]);
  const [page, setPage] = useState<number>(0);
  const [hasMore, setHasMore] = useState<boolean>(true);

  const mergeSignals = (s: MarketSignal[]) => {
    s.forEach(value => {
      if (!selectedStream || !value.streams.includes(selectedStream)) return;

      let found = false;
      for (const i in signals) {
        const s = signals[i];

        if (s._id === value._id) {
          signals[i] = value;
          found = true;
        }
      }

      if (!found) {
        signals.push(value);
        signals.sort((a, b) => dayjs(b.dateCreated).valueOf() - dayjs(a.dateCreated).valueOf());
        signals.pop();
      }
    });

    setSignals([...signals]);
    updateNotificationText([...signals]);
  };

  const loadMore = async () => {
    if (!signals || !selectedStream) return;

    const newPage = page + 1;
    const response = (await marketSignalApi.getSignalsByStream(selectedStream, newPage)).content;

    if (response.length > 0) {
      setSignals(prev => uniqueById([...prev, ...response]));
      updateNotificationText([...response]);
      setPage(newPage);
    } else {
      setHasMore(false);
    }
  };

  useSubscription<MarketSignal>('/topic/signals', message => {
    if (signals && selectedStream) {
      if (message.streams.includes(selectedStream)) {
        if (paused) {
          onMissedUpdate();
        } else {
          mergeSignals([message]);
        }
      }
    }
  });

  useTimer(
    60_000,
    () => {
      if (!selectedStream) return;

      if (!paused) {
        marketSignalApi.getSignalsByStream(selectedStream, 0).then(value => {
          mergeSignals(value.content);
        });
      }
    },
    false,
    'ms-panel-reload',
  );

  useEffect(() => {
    if (!selectedStream) return;

    if (!paused) {
      setPage(0);

      marketSignalApi.getSignalsByStream(selectedStream, 0).then(value => {
        setSignals(uniqueById(value.content));
        updateNotificationText(value.content);
      });
    }
    // eslint-disable-next-line
  }, [selectedStream, paused]);

  return (
    <div className={'h-100 overflow-auto'}>
      <InfiniscrollContainer onLoad={loadMore} hasMore={hasMore} variant="signal">
        {signals?.map(renderSignal)}
      </InfiniscrollContainer>
    </div>
  );
}
